// Ficha 09 — Entrega del equipo, variante CRÉDITO. Con el IMEI fijado en la firma el enrolamiento
// se abre solo; sin él (firmas viejas) se captura aquí. El poll de `…/device/enrollment` ve el cierre.
import { useEffect, useRef, useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { ArrowRight, Check } from 'lucide-react';
import { Button, Loading } from '@/kit';
import {
    useContractPreview,
    useDeviceEnrollment,
    useRegisterDevice,
    useWizardSession,
} from '../api/useWizard';
import { accountOrigin, alreadyHadAccount } from '../accountWait';
import { enrollmentView } from '../deviceEnrollment';
import {
    describeDeliveryTasks,
    enrollmentFlow,
    enrollmentGuideSteps,
    failedEnrollmentNotice,
    isGuideVisible,
    isSellerLockTask,
    sharedTasksFirstNumber,
} from '../enrollmentFlow';
import { PromotionBonusBanner } from '../components/PromotionBonusBanner';
import {
    deviceErrorBlamesImei,
    deviceErrorEnrollmentFailed,
    deviceErrorMessage,
} from '../deviceErrors';
import {
    IMEI_LENGTH,
    financedPhoneName,
    financedPhoneOf,
    pinnedFinancedImei,
} from '../deliveryPieces';
import { pinFailureAboutPieces } from '../piecesErrors';
import { LevelChip } from '../components/LevelChip';
import { WizardCard, WizardCardBody, WizardCardHead } from '../components/WizardCard';
import { WizardFootNav } from '../components/WizardFootNav';
import { InlineQueryError } from '../components/QueryErrorNotice';
import { DeliveryTasks } from '../components/delivery/DeliveryTasks';
import { DeviceEnrollmentGuide } from '../components/delivery/DeviceEnrollmentGuide';
import { DeviceRegisterError } from '../components/delivery/DeviceRegisterError';
import { FinancedDeviceStrip } from '../components/delivery/FinancedDeviceStrip';
import { EnrollmentStatusNotice } from '../components/delivery/EnrollmentStatusNotice';
import { LockStatusPanel } from '../components/delivery/LockStatusPanel';
import { SaleStatus } from '../components/delivery/SaleStatus';
import { TaskItem, type TaskState } from '../components/delivery/TaskItem';
import { useDeliveryTasks } from '../components/delivery/useDeliveryTasks';
import { IdentifierField } from '../components/delivery/IdentifierField';
import { PieceIdentifiers } from '../components/delivery/PieceIdentifiers';
import { useSaleClosing } from '../components/delivery/useSaleClosing';
import type { RegisterDeviceResult } from '../types';

function pendingSaleLabel(creditActive: boolean, piecesConfirmed: boolean): string {
    if (!creditActive) return 'La venta se registra al activar el crédito.';
    if (!piecesConfirmed) return 'La venta se registra al confirmar las demás piezas.';
    return 'Registrando la venta…';
}

export function CreditDeliveryStep({ sessionId }: { sessionId: string }) {
    const navigate = useNavigate();
    const { data: session, refresh } = useWizardSession(sessionId);
    const registerDevice = useRegisterDevice(sessionId);
    const financed = financedPhoneOf(session);

    const pinnedImei = pinnedFinancedImei(session);
    // Se decide al entrar: con el IMEI ya fijado (en la firma) aquí solo queda el candado.
    const [imeiFromSign, setImeiFromSign] = useState<boolean | undefined>(undefined);
    if (imeiFromSign === undefined && session) setImeiFromSign(pinnedImei !== '');
    const signPinned = imeiFromSign === true;
    // Sin tocar, el campo muestra el IMEI que ya quedó fijado en la sesión.
    const [imeiDraft, setImeiDraft] = useState<string | null>(null);
    const imei = imeiDraft ?? pinnedImei;
    const [device, setDevice] = useState<RegisterDeviceResult | null>(null);

    // El comprador que YA TRAÍA cuenta no recibe app ni tarjeta, pero el equipo financiado sí lleva
    // IMEI y candado: aquí se brincan las tareas compartidas, nunca el paso entero.
    const skipsSharedTasks = !!session && alreadyHadAccount(accountOrigin(session));
    const tasks = useDeliveryTasks(sessionId, session, !!session && !skipsSharedTasks);

    // El expediente del enrolamiento vive en el silo, no en esta pantalla: se consulta también al
    // reanudar, y por eso el QR sobrevive una recarga (`lockAppUrl` viaja en cada lectura).
    const creditId = session?.loan?.creditId;
    const enrollmentQuery = useDeviceEnrollment(sessionId, !!session && !!creditId);
    const preview = useContractPreview(sessionId, !!session && !!creditId);

    const enrollment = enrollmentQuery.data?.enrollment ?? device?.enrollment ?? null;
    const creditActive =
        enrollmentQuery.data?.creditStatus === 'ACTIVE' || preview.data?.credit.status === 'ACTIVE';
    // Un crédito ACTIVE ya trae `lockVerified` en su checklist 4/4: reanudar así no reabre la tarea.
    const view = enrollmentView(enrollmentQuery.data);
    const enrolled = view === 'CONFIRMED' || creditActive;
    const capturing = !enrollment && !creditActive;
    const registeredImei = enrollment?.imei ?? device?.enrollment.imei ?? '';
    const lockAppUrl = enrollmentQuery.data?.lockAppUrl ?? device?.lockAppUrl ?? '';
    // El proveedor viaja en la lectura desde antes de que haya IMEI, así que el paso sabe qué
    // enrolamiento le toca desde el primer render y nunca promete pasos de otro proveedor.
    const flow = enrollmentFlow(enrollmentQuery.data?.mdmProvider ?? device?.mdmProvider);
    const sellerInstallsLock = isSellerLockTask(flow);
    // 🔴 Hasta que la primera lectura llega no sabemos qué proveedor toca. La pantalla calla el
    // tramo del candado en vez de pintar los pasos del otro y retirarlos un instante después.
    const flowResolved = !enrollmentQuery.isLoading;

    // Con las piezas fijadas el poll cierra la venta; si no la cerró, complete-sale al ver ACTIVE.
    const closing = useSaleClosing(sessionId, session, refresh, {
        withFinancedPhone: true,
        canClose: creditActive,
        // Con el IMEI ya fijado, confirmar las demás piezas las fija con /pieces.
        pinOnConfirm: signPinned,
        closedSaleId: enrollmentQuery.data?.saleId,
    });
    const { saleRef } = closing;
    const saleClosed = !!saleRef;

    // El bono viaja solo en el tick que cerró su asiento, y ese es justo el que deja el enrolamiento
    // en CONFIRMED y corta el poll: la respuesta se queda quieta, así que no hay que retenerla.
    const bonus = enrollmentQuery.data?.promotionBonus;

    const openEnrollment = (value: string): void => {
        registerDevice.mutate(value, {
            onSuccess: (result) => {
                setDevice(result);
                void enrollmentQuery.refetch();
            },
            // El silo ya tiene el expediente en FAILED: se relee para mostrar el motivo.
            onError: (error) => {
                if (deviceErrorEnrollmentFailed(error)) void enrollmentQuery.refetch();
            },
        });
    };

    // Abre el enrolamiento una sola vez al entrar; el ref aguanta el doble montaje de StrictMode.
    const autoOpened = useRef(false);
    const autoOpen = signPinned && enrollmentQuery.isSuccess && !enrollment && !creditActive;
    useEffect(() => {
        if (!autoOpen || autoOpened.current) return;
        autoOpened.current = true;
        openEnrollment(pinnedImei);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [autoOpen]);

    if (!session) return <Loading size="lg" />;
    const level = session.loan?.level;
    const deviceError = registerDevice.isError ? registerDevice.error : null;
    const pinFailure = closing.pinFailure;
    const pinBlamesImei =
        !!pinFailure &&
        pinFailureAboutPieces(pinFailure) &&
        (pinFailure.target
            ? pinFailure.target.lineId === financed?.lineId &&
              pinFailure.target.sku === financed.sku
            : closing.pieces.length === 0);
    // Antes del IMEI se capturan las demás piezas: /pieces las fija todas juntas.
    const piecesPending = capturing && closing.pieces.length > 0 && !closing.piecesReady;
    const missingItems = enrollmentQuery.data?.missingItems ?? [];

    const imeiTaskState: TaskState = capturing ? 'active' : 'done';
    const lockTaskState: TaskState = enrolled
        ? 'done'
        : capturing && !signPinned
          ? 'blocked'
          : 'active';
    // 🔴 También exige el cierre: en Bienvenida se acredita la recarga, y acreditarla sobre una
    // venta sin registrar no tiene vuelta atrás.
    const canContinue = creditActive && saleClosed && (skipsSharedTasks || tasks.settled);

    // El alta de servidor no tiene «esperando el teléfono»: mientras el POST viaja se acusa recibo
    // con la misma franja de espera, y antes de eso el panel solo anuncia qué va a pasar.
    const idleLock = capturing && !registerDevice.isPending;
    const statusNotice = (
        <EnrollmentStatusNotice
            view={enrolled ? 'CONFIRMED' : (view ?? 'WAITING_INSTALL')}
            copy={flow.copy}
            failure={failedEnrollmentNotice(flow.copy, enrollment)}
            onRecheck={() => void enrollmentQuery.refetch()}
            onRetry={() => {
                openEnrollment(registeredImei);
            }}
            recheckPending={enrollmentQuery.isFetching}
            retryPending={registerDevice.isPending}
        />
    );
    const confirmDevice = (): void => {
        if (!financed) {
            openEnrollment(imei);
            return;
        }
        const value = imei;
        closing.pin(value, () => {
            openEnrollment(value);
        });
    };
    const imeiError =
        deviceError && deviceErrorBlamesImei(deviceError)
            ? deviceErrorMessage(deviceError)
            : pinBlamesImei
              ? pinFailure.message
              : undefined;
    const backToProduct = (): void => {
        void navigate({
            to: '/wizard/s/$sessionId/$step',
            params: { sessionId, step: 'product' },
        });
    };
    const deviceErrorNotice = deviceError && (
        <DeviceRegisterError
            error={deviceError}
            onRetry={() => {
                openEnrollment(pinnedImei);
            }}
            retrying={registerDevice.isPending}
            onBackToProduct={backToProduct}
        />
    );
    const otherPieces = (showConfirm: boolean) => (
        <PieceIdentifiers
            sessionId={sessionId}
            closing={closing}
            disabled={closing.closing || closing.pinning}
            showConfirm={showConfirm}
        />
    );

    return (
        <WizardCard>
            <WizardCardHead
                eyebrow="Entrega"
                title="Entrega del equipo"
                right={<LevelChip level={level} />}
                sub={
                    flowResolved
                        ? describeDeliveryTasks(flow, !skipsSharedTasks, !signPinned)
                        : undefined
                }
            />

            <WizardCardBody className="flex flex-col gap-3">
                {bonus && <PromotionBonusBanner bonus={bonus} />}
                {signPinned ? (
                    <FinancedDeviceStrip name={financedPhoneName(session)} imei={pinnedImei} />
                ) : (
                    <>
                        {/* Tarea 1 — IMEI: abre el enrolamiento, no lo termina. */}
                        <TaskItem
                            n={1}
                            state={imeiTaskState}
                            title="Registrar el IMEI del equipo"
                            desc="Se lee en el empaque o marcando *#06# en el equipo nuevo."
                        >
                            <div className="flex flex-col gap-2">
                                <IdentifierField
                                    sessionId={sessionId}
                                    lineId={financed?.lineId}
                                    sku={financed?.sku}
                                    mode="IMEI"
                                    label={financedPhoneName(session)}
                                    value={imei}
                                    onChange={(value) => {
                                        setImeiDraft(value);
                                        // Otra pieza quita la marca del rechazo anterior.
                                        if (deviceError && deviceErrorBlamesImei(deviceError)) {
                                            registerDevice.reset();
                                        }
                                        if (pinBlamesImei) closing.clearPinFailure();
                                    }}
                                    error={imeiError}
                                />
                                {imei.length < IMEI_LENGTH && (
                                    <p className="text-[11px] text-muted-foreground">
                                        {`Faltan ${String(IMEI_LENGTH - imei.length)} dígitos.`}
                                    </p>
                                )}
                                {capturing && closing.pieces.length > 0 && (
                                    <div className="flex flex-col gap-2 border-t border-border pt-2">
                                        <div className="text-[11.5px] text-muted-foreground">
                                            Escanea o teclea el IMEI o la serie de las demás piezas.
                                            Se fijan junto con el IMEI del equipo.
                                        </div>
                                        {otherPieces(false)}
                                    </div>
                                )}
                                {pinFailure && !pinFailureAboutPieces(pinFailure) && (
                                    <InlineQueryError
                                        className="text-[12px]"
                                        message={pinFailure.message}
                                        onRetry={closing.retry}
                                        retrying={closing.pinning}
                                    />
                                )}
                                {piecesPending && (
                                    <p className="text-[11px] text-muted-foreground">
                                        Primero identifica las demás piezas de la venta.
                                    </p>
                                )}
                                {deviceError && !deviceErrorBlamesImei(deviceError) && (
                                    <DeviceRegisterError
                                        error={deviceError}
                                        onRetry={confirmDevice}
                                        retrying={registerDevice.isPending}
                                        onBackToProduct={backToProduct}
                                    />
                                )}
                                <div className="flex justify-end">
                                    <Button
                                        size="sm"
                                        disabled={imei.length !== IMEI_LENGTH || piecesPending}
                                        isLoading={registerDevice.isPending || closing.pinning}
                                        onClick={confirmDevice}
                                    >
                                        Confirmar IMEI
                                    </Button>
                                </div>
                            </div>
                        </TaskItem>
                    </>
                )}

                {/* El candado: tarea numerada cuando alguien lo instala en el teléfono, y panel
                    sin número cuando el alta es de servidor y no le toca al vendedor. */}
                {!flowResolved ? (
                    <Loading size="sm" />
                ) : sellerInstallsLock ? (
                    <TaskItem
                        n={signPinned ? 1 : 2}
                        state={lockTaskState}
                        alwaysOpen
                        title={flow.title}
                        desc={flow.desc}
                    >
                        <div className="flex flex-col gap-3">
                            {signPinned && deviceError ? deviceErrorNotice : statusNotice}
                            {/* Cada flujo decide en qué vistas retira el instructivo. */}
                            {isGuideVisible(flow, {
                                view,
                                enrolled,
                                registeredImei,
                                signPinned,
                            }) && (
                                <DeviceEnrollmentGuide
                                    imei={registeredImei}
                                    steps={enrollmentGuideSteps(
                                        flow.guide,
                                        enrollmentQuery.data?.provisioningType,
                                    )}
                                    lockAppUrl={flow.guide.showsLockAppQr ? lockAppUrl : undefined}
                                    recoveryNote={flow.guide.recoveryNote}
                                />
                            )}
                        </div>
                    </TaskItem>
                ) : (
                    <LockStatusPanel title={flow.title} desc={flow.desc} done={enrolled}>
                        {/* Antes del IMEI no está pasando nada todavía: decir «esperando» ahí sería
                            inventarle al vendedor una espera que no empezó. */}
                        {signPinned && deviceError ? (
                            deviceErrorNotice
                        ) : idleLock && !signPinned && flow.idleNote ? (
                            <p className="text-[12.5px] leading-relaxed text-muted-foreground">
                                {flow.idleNote}
                            </p>
                        ) : (
                            statusNotice
                        )}
                    </LockStatusPanel>
                )}

                {/* El candado quedó confirmado pero el crédito sigue sin activar: lo que falta no es
                    del equipo (la firma o el enganche), y el vendedor tiene que leer cuál. */}
                {missingItems.length > 0 && (
                    <p className="rounded-md border border-warning-soft-border bg-warning-soft px-3 py-2 text-[11.5px] leading-relaxed text-warning">
                        El crédito aún no activa — falta: {missingItems.join(', ')}.
                    </p>
                )}

                {!skipsSharedTasks && flowResolved && (
                    <DeliveryTasks
                        tasks={tasks}
                        firstNumber={sharedTasksFirstNumber(flow, !signPinned)}
                        blocked={!creditActive}
                    />
                )}

                {/* Las demás piezas: con el IMEI de la firma se fijan con /pieces; con el equipo
                    enrolado por el front viejo se cierran con lines. */}
                {(signPinned || !capturing) && closing.pieces.length > 0 && !saleClosed && (
                    <section className="flex flex-col gap-2 rounded-[10px] border-[1.5px] border-border bg-card px-4 py-3.5">
                        <div>
                            <div className="text-[13.5px] font-semibold">
                                Identificar las demás piezas
                            </div>
                            <div className="mt-0.5 text-[11.5px] text-muted-foreground">
                                Escanea la serie de cada pieza.
                            </div>
                        </div>
                        {otherPieces(true)}
                        {pinFailure && !pinFailureAboutPieces(pinFailure) && (
                            <InlineQueryError
                                className="text-[12px]"
                                message={pinFailure.message}
                                onRetry={closing.retry}
                                retrying={closing.pinning}
                            />
                        )}
                    </section>
                )}

                {/* Cómo va el crédito y el cierre de la venta. Viven aquí y no en el pie: el pie es
                    solo navegación. */}
                <div className="flex flex-col gap-0.5">
                    <SaleStatus
                        saleRef={saleRef}
                        error={pinFailure ? null : closing.statusError}
                        retrying={closing.closing}
                        onRetry={closing.retry}
                        pendingLabel={pendingSaleLabel(creditActive, closing.piecesConfirmed)}
                    />
                    {creditActive && (
                        <span className="flex items-center gap-1 text-[11.5px] font-semibold text-success">
                            <Check className="size-3" strokeWidth={3} aria-hidden /> Crédito activo
                        </span>
                    )}
                </div>
            </WizardCardBody>

            <WizardFootNav>
                <Button
                    disabled={!canContinue}
                    rightIcon={<ArrowRight className="size-3.5" aria-hidden />}
                    onClick={() =>
                        void navigate({
                            to: '/wizard/s/$sessionId/$step',
                            params: { sessionId, step: 'referrals' },
                        })
                    }
                >
                    Continuar a Referidos
                </Button>
            </WizardFootNav>
        </WizardCard>
    );
}
